import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { AlertDialog } from '../components/AlertDialog';
import { ToastContainer } from '../components/ToastNotification';

interface ConfirmOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info' | 'success';
}

interface AlertOptions {
  title: string;
  message: string;
  variant?: 'success' | 'error' | 'info' | 'warning';
  confirmText?: string;
  copyableText?: string;
  copyLabel?: string;
}

interface ToastOptions {
  message: string;
  variant: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
}

interface Toast extends ToastOptions {
  id: string;
}

interface ModalContextValue {
  showConfirm: (options: ConfirmOptions) => Promise<boolean>;
  showAlert: (options: AlertOptions) => Promise<void>;
  showToast: (options: ToastOptions) => void;
  toast: {
    success: (message: string, duration?: number) => void;
    error: (message: string, duration?: number) => void;
    info: (message: string, duration?: number) => void;
    warning: (message: string, duration?: number) => void;
  };
}

const ModalContext = createContext<ModalContextValue | undefined>(undefined);

export function ModalProvider({ children }: { children: ReactNode }) {
  const [confirmState, setConfirmState] = useState<{
    isOpen: boolean;
    options: ConfirmOptions;
    resolve: (value: boolean) => void;
  } | null>(null);

  const [alertState, setAlertState] = useState<{
    isOpen: boolean;
    options: AlertOptions;
    resolve: () => void;
  } | null>(null);

  const [toasts, setToasts] = useState<Toast[]>([]);

  const showConfirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmState({ isOpen: true, options, resolve });
    });
  }, []);

  const handleConfirmClose = useCallback(() => {
    if (confirmState) {
      confirmState.resolve(false);
      setConfirmState(null);
    }
  }, [confirmState]);

  const handleConfirm = useCallback(() => {
    if (confirmState) {
      confirmState.resolve(true);
      setConfirmState(null);
    }
  }, [confirmState]);

  const showAlert = useCallback((options: AlertOptions): Promise<void> => {
    return new Promise((resolve) => {
      setAlertState({ isOpen: true, options, resolve });
    });
  }, []);

  const handleAlertClose = useCallback(() => {
    if (alertState) {
      alertState.resolve();
      setAlertState(null);
    }
  }, [alertState]);

  const showToast = useCallback((options: ToastOptions) => {
    const id = Math.random().toString(36).substring(2, 9);
    const newToast: Toast = { id, ...options };
    setToasts((prev) => [...prev, newToast]);
  }, []);

  const handleToastClose = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const toast = {
    success: (message: string, duration?: number) => showToast({ message, variant: 'success', duration }),
    error: (message: string, duration?: number) => showToast({ message, variant: 'error', duration }),
    info: (message: string, duration?: number) => showToast({ message, variant: 'info', duration }),
    warning: (message: string, duration?: number) => showToast({ message, variant: 'warning', duration }),
  };

  const value: ModalContextValue = {
    showConfirm,
    showAlert,
    showToast,
    toast,
  };

  return (
    <ModalContext.Provider value={value}>
      {children}

      {confirmState && (
        <ConfirmDialog
          isOpen={confirmState.isOpen}
          onClose={handleConfirmClose}
          onConfirm={handleConfirm}
          {...confirmState.options}
        />
      )}

      {alertState && (
        <AlertDialog
          isOpen={alertState.isOpen}
          onClose={handleAlertClose}
          {...alertState.options}
        />
      )}

      <ToastContainer toasts={toasts} onClose={handleToastClose} />
    </ModalContext.Provider>
  );
}

export function useModal() {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
}
